// frontend/src/components/crm/SendingProfiles.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useBranding } from '../../contexts/BrandingContext';
import { sendingProfilesAPI } from '../../utils/crmApi';
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    CheckCircleIcon,
    XCircleIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useDialog, ConfirmationDialog } from '../../components/kibo-ui/dialog';
import {
    TableProvider,
    Table,
    TableHeader,
    TableSearch,
    TableHead,
    TableBody,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableFooter,
    StatusBadge
} from '../../components/kibo-ui/table';

const SendingProfiles = () => {
    const { openDialog } = useDialog();
    const { branding } = useBranding();
    const navigate = useNavigate();
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

    useEffect(() => {
        loadProfiles();
    }, [search, pagination.page]);

    const loadProfiles = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(search && { search })
            };
            const response = await sendingProfilesAPI.getAll(params);
            if (response.data.success) {
                setProfiles(response.data.data || []);
                setPagination(prev => ({
                    ...prev,
                    total: response.data.pagination?.total || 0,
                    pages: response.data.pagination?.pages || 1
                }));
            }
        } catch (error) {
            console.error('Failed to load sending profiles:', error);
            toast.error('Failed to load sending profiles');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (profileId) => {
        openDialog({
            title: 'Delete Sending Profile',
            message: 'Are you sure you want to delete this sending profile? This action cannot be undone.',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            onConfirm: async () => {
                try {
                    await sendingProfilesAPI.delete(profileId);
                    toast.success('Sending profile deleted successfully');
                    loadProfiles();
                } catch (error) {
                    console.error('Failed to delete sending profile:', error);
                    toast.error('Failed to delete sending profile');
                }
            }
        });
    };

    const handleVerify = async (profileId) => {
        try {
            const response = await sendingProfilesAPI.verify(profileId, {});
            if (response.data.success) {
                toast.success('Domain verification started');
                loadProfiles();
            }
        } catch (error) {
            console.error('Failed to verify sending profile:', error);
            toast.error('Failed to verify sending profile');
        }
    };

    const getVerificationStatus = (profile) => {
        const verification = profile.verification || {};
        const domainStatus = verification.domain?.status;
        const dkimStatus = verification.dkim?.status;
        const spfStatus = verification.spf?.status;
        const dmarcStatus = verification.dmarc?.status;

        if (domainStatus === 'verified' && dkimStatus === 'verified' && spfStatus === 'verified') {
            return { status: 'verified', label: 'Verified', color: 'green' };
        } else if (domainStatus === 'pending' || dkimStatus === 'pending' || spfStatus === 'pending') {
            return { status: 'pending', label: 'Pending', color: 'yellow' };
        } else {
            return { status: 'unverified', label: 'Unverified', color: 'red' };
        }
    };

    const columns = [
        { accessor: 'name', label: 'Name', sortable: true },
        { accessor: 'domain', label: 'Domain', sortable: true },
        { accessor: 'fromEmail', label: 'From Email', sortable: true },
        { accessor: 'verification', label: 'Status', sortable: false },
        { accessor: 'isDefault', label: 'Default', sortable: false },
        { accessor: 'actions', label: 'Actions', sortable: false }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Sending Profiles</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Manage email sending domains and authentication settings
                    </p>
                </div>
                <div className="mt-4 sm:mt-0">
                    <Link
                        to="/sending-profiles/new"
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{
                            backgroundColor: branding.primaryColor,
                            '--tw-ring-color': branding.primaryColor
                        }}
                    >
                        <PlusIcon className="h-5 w-5 mr-2" />
                        Create Sending Profile
                    </Link>
                </div>
            </div>

            {/* Table */}
            <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
                <TableProvider data={profiles} columns={columns}>
                    <TableSearch
                        placeholder="Search sending profiles..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPagination(prev => ({ ...prev, page: 1 }));
                        }}
                    />
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeaderCell column={columns[0]}>Name</TableHeaderCell>
                                    <TableHeaderCell column={columns[1]}>Domain</TableHeaderCell>
                                    <TableHeaderCell column={columns[2]}>From Email</TableHeaderCell>
                                    <TableHeaderCell column={columns[3]}>Status</TableHeaderCell>
                                    <TableHeaderCell column={columns[4]}>Default</TableHeaderCell>
                                    <TableHeaderCell column={columns[5]}>Actions</TableHeaderCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                        Loading sending profiles...
                                    </TableCell>
                                </TableRow>
                            ) : profiles.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                        No sending profiles found. Create your first sending profile to get started.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                profiles.map((profile) => {
                                    const verificationStatus = getVerificationStatus(profile);
                                    return (
                                        <TableRow key={profile._id}>
                                            <TableCell>
                                                <div className="font-medium text-foreground">{profile.name}</div>
                                                {profile.description && (
                                                    <div className="text-sm text-muted-foreground mt-1">
                                                        {profile.description}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-foreground">{profile.domain}</div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-foreground">{profile.fromEmail}</div>
                                                {profile.fromName && (
                                                    <div className="text-sm text-muted-foreground">
                                                        {profile.fromName}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <StatusBadge
                                                    status={verificationStatus.status}
                                                    label={verificationStatus.label}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {profile.isDefault ? (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                                        Default
                                                    </span>
                                                ) : (
                                                    <span className="text-muted-foreground">-</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleVerify(profile._id)}
                                                        className="text-primary hover:text-primary/80"
                                                        title="Verify Domain"
                                                    >
                                                        <ArrowPathIcon className="h-5 w-5" />
                                                    </button>
                                                    <Link
                                                        to={`/sending-profiles/${profile._id}/edit`}
                                                        className="text-primary hover:text-primary/80"
                                                        title="Edit"
                                                    >
                                                        <PencilIcon className="h-5 w-5" />
                                                    </Link>
                                                    <button
                                                        onClick={() => handleDelete(profile._id)}
                                                        className="text-destructive hover:text-destructive/80"
                                                        title="Delete"
                                                    >
                                                        <TrashIcon className="h-5 w-5" />
                                                    </button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                            </TableBody>
                        </Table>
                    </div>
                    {pagination.pages > 1 && (
                        <div className="px-4 py-3 border-t border-border flex items-center justify-between">
                            <div className="text-sm text-muted-foreground">
                                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} profiles
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                    disabled={pagination.page === 1}
                                    className="px-3 py-1 border border-input rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                    disabled={pagination.page >= pagination.pages}
                                    className="px-3 py-1 border border-input rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </TableProvider>
            </div>
        </div>
    );
};

export default SendingProfiles;

